import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Image as ImageIcon,
  Search,
  X,
  Eye,
  EyeOff,
  Download,
  Upload,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'all', label: 'All' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'rooms', label: 'Rooms' },
  { value: 'dining', label: 'Dining' },
  { value: 'amenities', label: 'Amenities' },
  { value: 'nature', label: 'Nature' },
  { value: 'events', label: 'Events' },
  { value: 'attractions', label: 'Attractions' },
];

// Place tags for itinerary matching
const placeTags = [
  'deomali', 'duduma', 'kolab', 'jagannath', 'tribal', 
  'coffee', 'hotel', 'trek', 'nature', 'sunrise', 
  'village', 'food', 'spa', 'boat'
];

const defaultImage = {
  src: '',
  title: '',
  category: 'hotel',
  alt: '',
  place_tags: [],
  order: 0
};

export default function AdminGallery() {
  const { token } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState(defaultImage);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload/image`, uploadData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData({ ...formData, src: response.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data);
    } catch (error) {
      toast.error('Failed to fetch gallery images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.src.trim()) {
      toast.error('Image URL is required');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        alt: formData.alt || formData.title
      };

      if (editingImage) {
        await axios.put(`${API_URL}/api/gallery/${editingImage.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Image updated successfully');
      } else {
        await axios.post(`${API_URL}/api/gallery`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Image added successfully');
      }
      
      setIsDialogOpen(false);
      setEditingImage(null);
      setFormData(defaultImage);
      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save image');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      src: image.src,
      title: image.title,
      category: image.category,
      alt: image.alt || '',
      place_tags: image.place_tags || [],
      order: image.order || 0
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/gallery/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image deleted successfully');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleToggleActive = async (image) => {
    try {
      await axios.put(`${API_URL}/api/gallery/${image.id}`, 
        { is_active: !image.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(image.is_active ? 'Image hidden' : 'Image visible');
      fetchImages();
    } catch (error) {
      toast.error('Failed to update image');
    }
  };

  const handleSeedGallery = async () => {
    if (!window.confirm('This will seed default gallery images. Continue?')) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/gallery/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed gallery');
    }
  };

  const filteredImages = images.filter(image => {
    const matchesSearch = image.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         image.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || image.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="admin-gallery-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Gallery Management</h1>
          <p className="text-muted-foreground mt-1">{images.length} images total</p>
        </div>
        
        <div className="flex gap-2">
          {images.length === 0 && (
            <Button variant="outline" onClick={handleSeedGallery} data-testid="seed-gallery-btn">
              <Download className="w-4 h-4 mr-2" />
              Seed Default Images
            </Button>
          )}
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingImage(null);
              setFormData(defaultImage);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-emerald" data-testid="add-image-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Image
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingImage ? 'Edit Image' : 'Add New Image'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* File Upload Section */}
                <div className="space-y-3">
                  <Label>Upload Image</Label>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="image-upload"
                      data-testid="image-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File from Device
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Supported: JPEG, PNG, WebP, GIF (max 5MB)
                  </p>
                </div>

                <div className="relative flex items-center gap-3 py-2">
                  <div className="flex-1 h-px bg-border"></div>
                  <span className="text-xs text-muted-foreground">OR</span>
                  <div className="flex-1 h-px bg-border"></div>
                </div>

                {/* URL Input */}
                <div>
                  <Label>Image URL</Label>
                  <Input
                    value={formData.src}
                    onChange={(e) => setFormData({...formData, src: e.target.value})}
                    placeholder="https://example.com/image.jpg"
                    data-testid="image-src-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste an external URL or use uploaded image path
                  </p>
                </div>

                {formData.src && (
                  <div className="border rounded-lg overflow-hidden">
                    <img 
                      src={formData.src.startsWith('/') ? `${API_URL}${formData.src}` : formData.src} 
                      alt="Preview" 
                      className="w-full h-40 object-cover"
                      onError={(e) => e.target.src = '/images/placeholder.jpg'}
                    />
                  </div>
                )}

                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Luxury Suite View"
                    required
                    data-testid="image-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.filter(c => c.value !== 'all').map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Order</Label>
                    <Input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Alt Text (for accessibility)</Label>
                  <Input
                    value={formData.alt}
                    onChange={(e) => setFormData({...formData, alt: e.target.value})}
                    placeholder="Descriptive text for the image"
                  />
                </div>

                <div>
                  <Label>Place Tags (for Trip Planner)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {placeTags.map(tag => (
                      <Badge 
                        key={tag}
                        variant={formData.place_tags?.includes(tag) ? "default" : "outline"}
                        className={`cursor-pointer ${formData.place_tags?.includes(tag) ? 'bg-emerald-600' : ''}`}
                        onClick={() => {
                          const currentTags = formData.place_tags || [];
                          if (currentTags.includes(tag)) {
                            setFormData({...formData, place_tags: currentTags.filter(t => t !== tag)});
                          } else {
                            setFormData({...formData, place_tags: [...currentTags, tag]});
                          }
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Select tags to use this image in Trip Planner itineraries
                  </p>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-emerald" data-testid="save-image-btn">
                    {editingImage ? 'Update Image' : 'Add Image'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Tabs value={filterCategory} onValueChange={setFilterCategory}>
          <TabsList className="bg-muted flex-wrap h-auto">
            {categories.map((cat) => (
              <TabsTrigger key={cat.value} value={cat.value}>
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Images Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {filteredImages.map((image, index) => (
          <motion.div
            key={image.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.02 }}
          >
            <Card className={`overflow-hidden ${!image.is_active ? 'opacity-50' : ''}`} data-testid={`gallery-card-${image.id}`}>
              <div className="relative aspect-square group">
                <img
                  src={image.src}
                  alt={image.alt || image.title}
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.src = '/images/placeholder.jpg'}
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button 
                    size="icon" 
                    variant="secondary" 
                    onClick={() => handleEdit(image)}
                    data-testid={`edit-image-${image.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="secondary"
                    onClick={() => handleToggleActive(image)}
                  >
                    {image.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button 
                    size="icon" 
                    variant="destructive"
                    onClick={() => handleDelete(image.id)}
                    data-testid={`delete-image-${image.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Category Badge */}
                <Badge className="absolute top-2 left-2 capitalize bg-emerald-900/80">
                  {image.category}
                </Badge>
                
                {!image.is_active && (
                  <Badge variant="secondary" className="absolute top-2 right-2">
                    Hidden
                  </Badge>
                )}
              </div>
              <CardContent className="p-3">
                <p className="text-sm font-medium truncate">{image.title}</p>
                <p className="text-xs text-muted-foreground">Order: {image.order}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredImages.length === 0 && (
        <div className="text-center py-12">
          <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No images found</p>
          {images.length === 0 && (
            <Button variant="outline" className="mt-4" onClick={handleSeedGallery}>
              <Download className="w-4 h-4 mr-2" />
              Seed Default Images
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
